import {
	Alert,
	Box,
	Button,
	Card,
	Chip,
	Divider,
	Grid,
	Link,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import dayjs from 'dayjs';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { ONOFF_BOOLEAN, PAUSEORMOVEWAITING } from '@/common/constants/common';
import {
	LoginAccountEntity,
	LoginAccountTarget,
	LoginIdUnavailableReason,
} from '@/api/DTO/login-account/loginAccount.interface';
import {
	useCreateLoginAccountMutation,
	useGetLoginAccountCheckQuery,
	useGetLoginAccountQuery,
} from '@/api/login-account/loginAccountApi';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { showLabel } from '@/utils/helper';
import LoginIdHitsTable from './LoginIdHitsTable';
import ResetPasswordDialog from './ResetPasswordDialog';

export const PROGRAM_APP_LOGIN_TAB = 'programAppLogin';

const detailPath = (entity: LoginAccountEntity, id: string) =>
	entity === 'member'
		? `/member-management/member-list/detail/${id}`
		: `/family-management/family-management-list/detail/${id}`;

export const programAppLoginTabPath = (
	entity: LoginAccountEntity,
	id: string
) => `${detailPath(entity, id)}?tab=${PROGRAM_APP_LOGIN_TAB}`;

// Tab label with a warning icon while the login account is not activated.
// Shares the cached account query with the tab content.
export function ProgramAppLoginTabLabel(props: LoginAccountTarget) {
	const { entity, id } = props;
	const { t } = useTranslation();
	const { data } = useGetLoginAccountQuery({ entity, id }, { skip: !id });
	const notActivated = !!data?.data && !data.data.activated;

	return (
		<Stack direction='row' alignItems='center' spacing={0.5}>
			<Typography variant='body1'>{t('loginAccount.tab')}</Typography>
			{notActivated && (
				<Tooltip title={t('loginAccount.notActivated')}>
					<ReportProblemOutlinedIcon color='warning' fontSize='small' />
				</Tooltip>
			)}
		</Stack>
	);
}

interface Props {
	entity: LoginAccountEntity;
	id: string;
	hasHkid: boolean;
	hasPhone: boolean;
	canEdit: boolean;
	/** CHA-005 §九: the linked record on the other side shares this account. */
	linkedRecord?: {
		linkedTo: 'member' | 'familyMember';
		fileNo?: string | null;
		href?: string;
	};
}

export default function ProgramAppLoginTab(props: Props) {
	const { entity, id, hasHkid, hasPhone, canEdit, linkedRecord } = props;
	const { t } = useTranslation();
	const showModal = useModal();
	const navigate = useNavigate();
	const target: LoginAccountTarget = { entity, id };
	const isFamilyMember = entity === 'family-member';

	const [resetOpen, setResetOpen] = useState(false);
	const goToEditLoginId = () =>
		navigate(
			`${detailPath(entity, id)}/${isFamilyMember ? 'edit-login-id' : 'login-id'}`
		);

	const { data: accountResponse } = useGetLoginAccountQuery(target, {
		skip: !id,
		refetchOnMountOrArgChange: true,
	});
	const account = accountResponse?.data;
	const hasAccount = !!account?.loginId;

	// The default Login ID check is only needed while there is no account yet.
	// The family member check answers 400 when the phone is empty, so the empty
	// phone case is decided locally instead of calling it.
	const phoneEmptyLocally = isFamilyMember && !hasPhone;
	const { data: checkResponse } = useGetLoginAccountCheckQuery(target, {
		skip: !id || !account || hasAccount || !canEdit || phoneEmptyLocally,
		refetchOnMountOrArgChange: true,
	});
	const check = checkResponse?.data;
	// The taken alert has its own "enter Login ID" button, so the header Edit
	// button is hidden meanwhile to avoid two buttons doing the same thing.
	const defaultTaken =
		!hasAccount &&
		check?.unavailableReason === LoginIdUnavailableReason.LoginIdTaken;

	const [createLoginAccount] = useCreateLoginAccountMutation();

	const handleCreate = async () => {
		const response = await createLoginAccount(target);
		if ('data' in response) {
			enqueueSnackbar(t('loginAccount.createSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	const confirmCreate = () => {
		showModal((modalProps: any) => (
			<ConfirmationDialog
				{...modalProps}
				showCloseIcon={true}
				title={t('loginAccount.createConfirmTitle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('loginAccount.createConfirmText', {
							loginId: check?.defaultLoginId,
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ variant: 'contained' }}
				onConfirm={handleCreate}
			/>
		));
	};

	const handleLink = () => {
		showModal((modalProps: any) => (
			<ConfirmationDialog
				{...modalProps}
				showCloseIcon={true}
				title={t('loginAccount.linkConfirmTitle')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('loginAccount.linkConfirmText')}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ variant: 'contained' }}
				onConfirm={handleCreate}
			/>
		));
	};

	const renderNotActivated = () => {
		if (!account || account.activated) return null;

		if (hasAccount) {
			return <Alert severity='warning'>{t('loginAccount.noPassword')}</Alert>;
		}

		if (!canEdit) {
			return <Alert severity='warning'>{t('loginAccount.noAccount')}</Alert>;
		}

		if (
			phoneEmptyLocally ||
			check?.unavailableReason === LoginIdUnavailableReason.PhoneEmpty
		) {
			return <Alert severity='warning'>{t('loginAccount.phoneEmpty')}</Alert>;
		}

		if (!check) return null;

		if (check.loginIdAvailable) {
			return (
				<Alert
					severity='warning'
					action={
						<Button
							variant='contained'
							size='small'
							onClick={confirmCreate}
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
						>
							{t('loginAccount.createAccount')}
						</Button>
					}
				>
					{t('loginAccount.defaultAvailable', {
						loginId: check.defaultLoginId,
					})}
				</Alert>
			);
		}

		return (
			<Alert
				severity='warning'
				sx={{ '& .MuiAlert-message': { width: '100%' } }}
			>
				<Stack spacing={2}>
					<Typography variant='body2'>
						{t(
							check.canLink
								? 'loginAccount.defaultTaken'
								: 'loginAccount.defaultTakenCannotLink',
							{ loginId: check.defaultLoginId }
						)}
					</Typography>
					<LoginIdHitsTable hits={check.hits} />
					<Stack direction='row' spacing={2} justifyContent='flex-end'>
						<Button
							variant='outlined'
							size='small'
							sx={{ textTransform: 'none' }}
							onClick={goToEditLoginId}
						>
							{check.canLink
								? t('loginAccount.notSamePerson')
								: t('loginAccount.enterLoginId')}
						</Button>
						{check.canLink && (
							<Button
								variant='contained'
								size='small'
								sx={{ textTransform: 'none' }}
								onClick={handleLink}
							>
								{t('loginAccount.samePerson')}
							</Button>
						)}
					</Stack>
				</Stack>
			</Alert>
		);
	};

	return (
		<>
			<Card sx={{ padding: '24px', marginTop: 3 }}>
				<Box display='flex' justifyContent='space-between' alignItems='center'>
					<Typography sx={{ fontWeight: 'bold' }}>
						{t('loginAccount.loginAccount')}
					</Typography>
					{canEdit && account && !defaultTaken && (
						<Stack direction='row' spacing={2}>
							<Button
								variant='outlined'
								sx={{ textTransform: 'none' }}
								onClick={goToEditLoginId}
							>
								{t('button.edit')}
							</Button>
							{hasAccount && (
								<Button
									variant='outlined'
									sx={{ textTransform: 'none' }}
									onClick={() => setResetOpen(true)}
								>
									{t('loginAccount.resetPassword')}
								</Button>
							)}
						</Stack>
					)}
				</Box>

				<Stack spacing={2} sx={{ marginTop: 2 }}>
					{account?.sharedWithKat && (
						<Alert severity='info'>{t('loginAccount.sharedWithKat')}</Alert>
					)}
					{linkedRecord && (
						<Alert severity='info'>
							<Trans
								i18nKey={
									linkedRecord.linkedTo === 'member'
										? 'loginAccount.sharedWithLinkedMember'
										: 'loginAccount.sharedWithLinkedFamilyMember'
								}
								values={{ fileNo: linkedRecord.fileNo || '-' }}
								components={{
									anchor: linkedRecord.href ? (
										<Link
											href={linkedRecord.href}
											target='_blank'
											rel='noopener noreferrer'
											color='#1BBED7'
										/>
									) : (
										<span />
									),
								}}
							/>
						</Alert>
					)}
					{renderNotActivated()}
				</Stack>

				<Grid container spacing={3} marginTop={0} marginBottom={3}>
					<Grid size={12}>
						<AppFieldView
							label={t('loginAccount.status')}
							valueElement={
								account ? (
									<Chip
										size='small'
										color={account.activated ? 'success' : 'default'}
										label={
											account.activated
												? t('loginAccount.activated')
												: t('loginAccount.notActivated')
										}
									/>
								) : undefined
							}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('loginAccount.loginId')}
							value={account?.loginId || ''}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('loginAccount.password')}
							valueElement={
								<Typography variant='caption' color='common.neutral50'>
									({t('loginAccount.passwordHintText')})
								</Typography>
							}
						/>
					</Grid>
				</Grid>

				<Divider sx={{ marginBottom: 3 }} />
				<Box display='flex' justifyContent='space-between' alignItems='center'>
					<Typography sx={{ fontWeight: 'bold' }}>
						{t('loginAccount.programAppSettings')}
					</Typography>
					{canEdit && (
						<Button
							variant='outlined'
							sx={{ textTransform: 'none' }}
							disabled={!hasAccount}
							onClick={() =>
								navigate(`${detailPath(entity, id)}/edit-program-app-settings`)
							}
						>
							{t('button.edit')}
						</Button>
					)}
				</Box>
				{account && !hasAccount && canEdit && (
					<Stack
						direction='row'
						alignItems='center'
						spacing={0.5}
						sx={{ marginTop: 0.5, color: 'text.secondary' }}
					>
						<InfoOutlinedIcon sx={{ fontSize: 16 }} />
						<Typography variant='caption'>
							{t('loginAccount.settingsNeedAccount')}
						</Typography>
					</Stack>
				)}
				<Grid container spacing={3} marginTop={0} marginBottom={3}>
					<Grid size={12}>
						<AppFieldView
							label={t('member.notificationProgramApp')}
							value={
								account?.notificationProgramApp == null
									? ''
									: showLabel(account.notificationProgramApp, ONOFF_BOOLEAN)
							}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('member.pauseEnrolmentMoveWaitingList')}
							value={
								account?.pauseOrMoveWaiting == null
									? ''
									: showLabel(account.pauseOrMoveWaiting, PAUSEORMOVEWAITING)
							}
						/>
					</Grid>
					<Grid size={6}>
						<AppFieldView
							label={t('member.endDatePauseEnrolmentWaitingList')}
							value={
								account?.endDateOfPauseEnrollment
									? dayjs(account.endDateOfPauseEnrollment)
											.locale('en')
											.format(DISPLAY_DATE_FORMAT)
									: ''
							}
						/>
					</Grid>
				</Grid>
			</Card>

			<ResetPasswordDialog
				open={resetOpen}
				onClose={() => setResetOpen(false)}
				target={target}
				hasHkid={hasHkid}
				sharedWithKat={!!account?.sharedWithKat}
			/>
		</>
	);
}
